import { useEloApi } from '@/api/api';
import { Patient } from '@/api/patients';
import { useAuth0 } from '@auth0/auth0-react';
import { Dispatch, SetStateAction, createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const defaultPatient = null;

export type SetPatient = Dispatch<SetStateAction<Patient | null>>;
const defaultSetPatient: SetPatient = () => {};
export type SetPatients = Dispatch<SetStateAction<Patient[]>>;
const defaultSetPatients: SetPatients = () => {
  return [];
};
export const PatientContext = createContext<{
  patient: Patient | null;
  setPatient: SetPatient;
  patients: Patient[];
  isLoading: boolean;
  setPatients: SetPatients;
  reloadPatients: () => Promise<void>;
}>({
  patient: defaultPatient,
  setPatient: defaultSetPatient,
  patients: [],
  isLoading: false,
  setPatients: defaultSetPatients,
  reloadPatients: async () => {},
});

export function PatientProvider(props: any) {
  const { patientId } = useParams();
  const { user, getAccessTokenSilently, isLoading: isLoadingAuth0 } = useAuth0();
  const api = useEloApi({ getAccessTokenSilently });
  const [patient, setPatient] = useState<Patient | null>(defaultPatient);
  const [patients, setPatients] = useState<Patient[]>([]);
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const reloadPatients = async () => {
    if (user === undefined) {
      // not logged in yet
      return;
    }

    setIsLoading(true);
    const patients = await api.patientsList();
    setIsLoading(false);
    if (location.pathname === '/' && patient == null && patients.length > 0) {
      navigate(`/patients/${patients[0].id}/dashboard`);
    } else if (patients && '' + patient?.id !== patientId) {
      setPatient(patients.find((pat) => '' + pat.id === patientId)!);
    }
    setPatients(patients);
  };

  useEffect(() => {
    reloadPatients();
  }, [user, location, patientId]);

  return (
    <PatientContext.Provider
      value={{ patient, setPatient, patients, isLoading, setPatients, reloadPatients }}
    >
      {props.children}
    </PatientContext.Provider>
  );
}

export const usePatient = () => {
  return useContext(PatientContext);
};
